import { INCREMENTAL_SPEECH_PROTOCOL, INCREMENTAL_SPEECH_LIMITS as LIMITS } from "../../supabase/functions/_shared/incrementalSpeechProtocol";
import { PROGRESSIVE_SPEECH_ERROR_CODES, type ProgressiveSpeechErrorCode } from "../../supabase/functions/_shared/progressiveSpeechProtocol";
import { ProgressiveSpeechFailure, type OrderedSpeechPlayer } from "./mirrorProgressiveSpeech";
import type { SpeechDeliveryStage } from "../../supabase/functions/_shared/speechDeliveryTiming";

/** Local content-free observations; never sent as complete-clip analytics. */
export type MirrorIncrementalSpeechObservation =
  | { stage: "admitted" } | { stage: "accepted" } | { stage: "json_fallback" }
  | { stage: "first_audio_ready"; cached: boolean | null }
  | { stage: "playing" } | { stage: "stream_done" } | { stage: "ended" }
  | { stage: "error"; code: ProgressiveSpeechFailure["code"] }
  | { stage: "cancelled"; code: "stop" | "superseded" | "unmount" | "owner_changed" };
export type MirrorIncrementalSpeechObserver = (event: MirrorIncrementalSpeechObservation) => void;
export interface SpeechAudioPart { seq: number; part: number; bytes: Uint8Array; cached: boolean | null }
function fail(code: ConstructorParameters<typeof ProgressiveSpeechFailure>[0]): never { throw new ProgressiveSpeechFailure(code); }
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const keys = (value: Record<string, unknown>, names: readonly string[]) =>
  Object.keys(value).length === names.length && names.every(name => Object.prototype.hasOwnProperty.call(value, name));
function decodeAudio(value: unknown, maximum: number): Uint8Array {
  if (typeof value !== "string" || !value.length || value.length > Math.ceil(maximum / 3) * 4
    || value.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(value)) fail("invalid_audio");
  let binary: string;
  try { binary = atob(value as string); } catch { return fail("invalid_audio"); }
  if (!binary.length || binary.length > maximum || btoa(binary) !== value) fail("invalid_audio");
  return Uint8Array.from(binary, char => char.charCodeAt(0));
}

/** Select only a positively supported MPEG implementation, without UA assumptions.
 * iPhone WKWebView exposes ManagedMediaSource instead of MediaSource. Its source
 * opens only with remote playback disabled (we have no alternate AirPlay URL). */
function incrementalMediaSource(): { constructor: typeof MediaSource; managed: boolean } | null {
  const runtime = globalThis as typeof globalThis & { ManagedMediaSource?: typeof MediaSource };
  for (const name of ["MediaSource", "ManagedMediaSource"] as const) {
    try {
      const constructor = runtime[name], managed = name === "ManagedMediaSource";
      if (typeof constructor !== "function" || constructor.isTypeSupported("audio/mpeg") !== true) continue;
      if (managed && (typeof HTMLMediaElement === "undefined" || !("disableRemotePlayback" in HTMLMediaElement.prototype))) continue;
      return { constructor, managed };
    } catch { /* An unavailable implementation must not hide the next candidate. */ }
  }
  return null;
}
export function supportsIncrementalMp3(): boolean {
  return incrementalMediaSource() !== null;
}

interface IncrementalSpeechReadOptions {
  signal: AbortSignal; assertCurrent: () => void;
  accepted: () => void; legacy: () => void; streamDone: () => void;
  deliveryPoint?: (stage: SpeechDeliveryStage) => void;
}

/** Own the captured body immediately: async-generator finally does not run if
 * its return happens before the first next (for example while sourceopen waits). */
export function readIncrementalSpeech(response: Response, options: IncrementalSpeechReadOptions): AsyncGenerator<SpeechAudioPart> {
  const iterator = readIncrementalSpeechParts(response, options);
  let disposed = false;
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    options.signal.removeEventListener("abort", dispose);
    // An active reader owns its locked body and its own abort/finally cleanup.
    // Before admission, cancel this captured response even if fetch ignores abort.
    if (response.body && !response.body.locked) void response.body.cancel().catch(() => {});
  };
  if (options.signal.aborted) dispose();
  else options.signal.addEventListener("abort", dispose, { once: true });
  const next = iterator.next.bind(iterator), close = iterator.return.bind(iterator), raise = iterator.throw.bind(iterator);
  iterator.next = async (...args) => {
    try { const result = await next(...args); if (result.done) dispose(); return result; }
    catch (error) { dispose(); throw error; }
  };
  iterator.return = async (...args) => { dispose(); return close(...args); };
  iterator.throw = async (...args) => { dispose(); return raise(...args); };
  return iterator;
}

/** Negotiated v2 only. A same-response legacy JSON fallback never starts another synthesis. */
async function* readIncrementalSpeechParts(response: Response, options: IncrementalSpeechReadOptions): AsyncGenerator<SpeechAudioPart> {
  const guard = () => { options.assertCurrent(); if (options.signal.aborted) fail("cancelled"); };
  const point = (stage: SpeechDeliveryStage) => { try { options.deliveryPoint?.(stage); } catch { /* Optional diagnostics. */ } };
  let sse = false;
  try {
    guard();
    if (!response.ok) fail(response.status === 402 ? "quota_rejected" : "stream_failed");
    if (!response.body) fail("stream_failed");
    const mime = response.headers.get("content-type") ?? "";
    sse = /^text\/event-stream(?:;|$)/i.test(mime);
    if (!sse && !/^application\/json(?:;|$)/i.test(mime)) fail("protocol");
    if (sse) options.accepted(); else options.legacy();
  } catch (error) { void response.body?.cancel().catch(() => {}); throw error; }
  const reader = response.body!.getReader(), decoder = new TextDecoder("utf-8", { fatal: true });
  let rejectRead: ((error: Error) => void) | null = null;
  const abortReader = () => { rejectRead?.(new ProgressiveSpeechFailure("cancelled")); void reader.cancel().catch(() => {}); };
  options.signal.addEventListener("abort", abortReader, { once: true });
  let buffer = "", wireBytes = 0, totalBytes = 0, clipBytes = 0;
  let count: number | null = null, seq = 0, part = 0, cached: boolean | null = null, done = false, transportStarted = false, reconstructed = false;
  const maxWire = Math.ceil(LIMITS.totalAudioBytes / 3) * 4 + LIMITS.clipCount * LIMITS.chunkCount * 1024;
  try {
    while (true) {
      guard();
      if (sse) {
        const separator = /\r?\n\r?\n/.exec(buffer);
        if (separator) {
          const frame = buffer.slice(0, separator.index);
          buffer = buffer.slice(separator.index + separator[0].length);
          if (new TextEncoder().encode(frame).length > LIMITS.frameBytes) fail("protocol");
          const data: string[] = [];
          for (const line of frame.split(/\r?\n/)) {
            if (!line || line.startsWith(":")) continue;
            if (!line.startsWith("data:")) fail("protocol");
            data.push(line.slice(5).replace(/^ /, ""));
          }
          if (!data.length) continue;
          if (done) fail("protocol");
          let event: unknown;
          try { event = JSON.parse(data.join("\n")); } catch { fail("protocol"); }
          if (!object(event) || event.protocol !== INCREMENTAL_SPEECH_PROTOCOL) fail("protocol");
          if (event.type === "start") {
            if (count !== null || !keys(event, ["type", "protocol", "clips"]) || !Number.isInteger(event.clips)
              || Number(event.clips) < 1 || Number(event.clips) > LIMITS.clipCount) fail("protocol");
            count = Number(event.clips); continue;
          }
          if (event.type === "error") {
            if (!keys(event, ["type", "protocol", "code"]) || !PROGRESSIVE_SPEECH_ERROR_CODES.some(code => code === event.code)) fail("protocol");
            fail(event.code as ProgressiveSpeechErrorCode);
          }
          if (count === null) fail("protocol");
          if (event.type === "done") {
            if (!keys(event, ["type", "protocol", "clips"]) || event.clips !== count || seq !== count || part !== 0) fail("protocol");
            done = true; options.streamDone(); continue;
          }
          if (event.type === "clip_done") {
            if (!keys(event, ["type", "protocol", "seq", "parts"]) || seq >= count || event.seq !== seq || !part || event.parts !== part) fail("protocol");
            seq++; part = 0; clipBytes = 0; cached = null; continue;
          }
          if (event.type !== "audio" || !keys(event, ["type", "protocol", "seq", "part", "audio_base64", "cached", "credit_balance_units"])
            || seq >= count || event.seq !== seq || event.part !== part || part >= LIMITS.chunkCount || typeof event.cached !== "boolean"
            || (part > 0 && event.cached !== cached) || (part > 0 && event.credit_balance_units !== null)
            || !(event.credit_balance_units === null || typeof event.credit_balance_units === "number"
              && Number.isSafeInteger(event.credit_balance_units) && event.credit_balance_units >= 0)) fail("protocol");
          const bytes = decodeAudio(event.audio_base64, LIMITS.chunkAudioBytes);
          clipBytes += bytes.length; totalBytes += bytes.length;
          if (clipBytes > LIMITS.clipAudioBytes || totalBytes > LIMITS.totalAudioBytes) fail("invalid_audio");
          cached = event.cached;
          guard();
          if (!reconstructed) { reconstructed = true; point("first_mp3_bytes_reconstructed"); }
          yield { seq, part: part++, bytes, cached }; guard(); continue;
        }
        if (new TextEncoder().encode(buffer).length > LIMITS.frameBytes) fail("protocol");
      } else if (wireBytes > Math.ceil(LIMITS.clipAudioBytes / 3) * 4 + 1024) fail("protocol");
      let timer: ReturnType<typeof setTimeout> | undefined;
      const result = await Promise.race([
        reader.read(),
        new Promise<never>((_, reject) => { rejectRead = reject; }),
        new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new ProgressiveSpeechFailure("timeout")), LIMITS.requestMs); }),
      ]).finally(() => { rejectRead = null; if (timer !== undefined) clearTimeout(timer); });
      guard();
      if (result.done) { point("transport_eof"); buffer += decoder.decode(); break; }
      if (result.value.byteLength > 0 && !transportStarted) { transportStarted = true; point("first_nonempty_transport_chunk"); }
      wireBytes += result.value.byteLength;
      if (wireBytes > maxWire) fail("protocol");
      buffer += decoder.decode(result.value, { stream: true });
    }
    if (sse) {
      if (buffer.trim() || !done || count === null || seq !== count || part !== 0) fail("protocol");
    } else {
      let value: unknown;
      try { value = JSON.parse(buffer); } catch { fail("protocol"); }
      if (!object(value)) fail("protocol");
      const bytes = decodeAudio(value.audio_base64, LIMITS.clipAudioBytes);
      guard(); point("first_mp3_bytes_reconstructed");
      for (let offset = 0, index = 0; offset < bytes.length; offset += LIMITS.chunkAudioBytes, index++) {
        guard(); yield { seq: 0, part: index, bytes: bytes.slice(offset, offset + LIMITS.chunkAudioBytes), cached: typeof value.cached === "boolean" ? value.cached : null };
      }
      guard();
    }
  } finally {
    options.signal.removeEventListener("abort", abortReader); rejectRead = null;
    void reader.cancel().catch(() => {});
    try { reader.releaseLock(); } catch { /* Captured pending read cleanup. */ }
  }
}

/** A single MPEG SourceBuffer accepts parts before clip/transport completion.
 * Append completion and HTML playing are software observations, never heard sound. */
export function playIncrementalSpeech(options: {
  source: AsyncIterable<SpeechAudioPart>; audio: HTMLAudioElement; signal: AbortSignal;
  assertCurrent: () => void; ownsAudio: () => boolean; abortSource: () => void;
  setPlaybackMode: () => Promise<unknown>;
  sourceChanged: (url: string) => void; loading: (value: boolean) => void; playing: (value: boolean) => void;
  observation?: (event: MirrorIncrementalSpeechObservation) => void;
  firstReady?: (cached: boolean | null) => void; firstPlaying?: () => void;
  complete: () => void; failed: (error: ProgressiveSpeechFailure) => void;
}): OrderedSpeechPlayer {
  const iterator = options.source[Symbol.asyncIterator](), pending = new Set<(error: Error) => void>();
  let media: MediaSource | null = null, sourceBuffer: SourceBuffer | null = null, url: string | null = null;
  let terminal = false, sourceDone = false, started = false, first = false, playingObserved = false;
  let nextSeq = 0, nextPart = 0, totalBytes = 0, clipBytes = 0;
  let remotePlaybackPolicy: { disabled: boolean; attribute: string | null; originalSource: string } | null = null;
  const restoreRemotePlayback = () => {
    if (!remotePlaybackPolicy) return;
    const policy = remotePlaybackPolicy; remotePlaybackPolicy = null;
    try {
      options.audio.disableRemotePlayback = policy.disabled;
      if (policy.attribute === null) options.audio.removeAttribute("disableremoteplayback");
      else options.audio.setAttribute("disableremoteplayback", policy.attribute);
    } catch { /* A detached element must not interrupt captured-source cleanup. */ }
  };
  let firstResolve!: () => void, firstReject!: (error: Error) => void, finishResolve!: () => void;
  const firstStarted = new Promise<void>((resolve, reject) => { firstResolve = resolve; firstReject = reject; });
  const finished = new Promise<void>(resolve => { finishResolve = resolve; });
  const current = () => {
    if (terminal || options.signal.aborted) return false;
    try { options.assertCurrent(); return true; } catch { return false; }
  };
  const owned = (requireCurrent = false) => current() && options.ownsAudio() && url !== null && options.audio.src === url
    && (!requireCurrent || options.audio.currentSrc === url);
  const observe = (event: MirrorIncrementalSpeechObservation) => {
    if (!current()) return;
    try { void Promise.resolve(options.observation?.(event)).catch(() => {}); } catch { /* Optional telemetry. */ }
  };
  const dispose = () => {
    for (const reject of pending) reject(new ProgressiveSpeechFailure("cancelled"));
    pending.clear();
    try { options.abortSource(); } catch { /* Captured request only. */ }
    if (sourceBuffer && media) {
      try { if (sourceBuffer.updating) sourceBuffer.abort(); } catch { /* Detached source. */ }
      try { if (media.readyState === "open") media.removeSourceBuffer(sourceBuffer); } catch { /* Detached source. */ }
    }
    if (options.ownsAudio() && url !== null && options.audio.src === url) {
      const audio = options.audio;
      audio.pause(); audio.onplay = audio.onplaying = audio.onended = audio.onerror = null;
      audio.removeAttribute("src"); restoreRemotePlayback(); audio.load();
    } else if (options.ownsAudio() && remotePlaybackPolicy && options.audio.src === remotePlaybackPolicy.originalSource) {
      // A policy setter/attachment may fail before this player owns the URL.
      restoreRemotePlayback();
    }
    remotePlaybackPolicy = null;
    if (url !== null) URL.revokeObjectURL(url);
    sourceBuffer = null; media = null; url = null;
    void iterator.return?.().catch(() => {}); finishResolve();
  };
  const stop = (reason: "stop" | "superseded" | "unmount" | "owner_changed" = "stop") => {
    if (terminal) return;
    if (reason !== "owner_changed") observe({ stage: "cancelled", code: reason });
    terminal = true; if (!started) firstResolve(); dispose();
  };
  const failed = (error: unknown) => {
    if (terminal) return;
    if (!current()) { stop("owner_changed"); return; }
    const safe = error instanceof ProgressiveSpeechFailure ? error : new ProgressiveSpeechFailure("stream_failed");
    observe({ stage: "error", code: safe.code });
    terminal = true; if (!started) firstReject(safe); dispose(); options.failed(safe);
  };
  const complete = () => {
    if (!owned(true) || !sourceDone || !options.audio.ended) return;
    observe({ stage: "ended" }); terminal = true; if (!started) firstResolve(); dispose(); options.complete();
  };
  const waitEvent = (target: EventTarget, success: string, errorNames: string[], action?: () => void): Promise<void> => new Promise((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      pending.delete(rejected); clearTimeout(timer);
      target.removeEventListener(success, accepted);
      for (const name of errorNames) target.removeEventListener(name, errored);
    };
    const settle = (error?: Error) => { if (settled) return; settled = true; cleanup(); if (error) reject(error); else resolve(); };
    const rejected = (error: Error) => settle(error);
    const accepted = () => { if (!owned()) settle(new ProgressiveSpeechFailure("cancelled")); else settle(); };
    const errored = () => settle(new ProgressiveSpeechFailure("audio_error"));
    const timer = setTimeout(() => settle(new ProgressiveSpeechFailure("timeout")), LIMITS.providerMs);
    pending.add(rejected); target.addEventListener(success, accepted);
    for (const name of errorNames) target.addEventListener(name, errored);
    try { action?.(); } catch { settle(new ProgressiveSpeechFailure("audio_error")); }
  });
  const admitPlayback = async () => {
    await options.setPlaybackMode();
    if (!owned()) { if (!terminal) stop("owner_changed"); return; }
    await options.audio.play();
    if (!owned()) { if (!terminal) stop("owner_changed"); return; }
    options.loading(false); if (!started) { started = true; firstResolve(); }
  };
  const pump = async () => {
    try {
      if (!current()) { stop("owner_changed"); return; }
      const implementation = incrementalMediaSource();
      if (!implementation) fail("playback");
      media = new implementation.constructor();
      url = URL.createObjectURL(media); options.sourceChanged(url);
      if (!current() || !options.ownsAudio()) { stop("owner_changed"); return; }
      const audio = options.audio;
      if (implementation.managed) {
        remotePlaybackPolicy = { disabled: audio.disableRemotePlayback, attribute: audio.getAttribute("disableremoteplayback"), originalSource: audio.src };
        audio.disableRemotePlayback = true;
        audio.setAttribute("disableremoteplayback", "");
        if (audio.disableRemotePlayback !== true) fail("playback");
      }
      audio.onplay = () => { if (owned()) options.playing(true); };
      audio.onplaying = () => {
        if (!owned(true) || playingObserved) return;
        playingObserved = true; observe({ stage: "playing" }); options.firstPlaying?.();
      };
      audio.onended = complete;
      audio.onerror = () => { if (owned()) failed(new ProgressiveSpeechFailure("audio_error")); };
      // Register sourceopen before attaching the URL: no lost synchronous event.
      const opening = waitEvent(media, "sourceopen", ["sourceclose"]);
      audio.src = url; audio.load(); await opening;
      if (!owned() || !media || media.readyState !== "open") fail("cancelled");
      sourceBuffer = media.addSourceBuffer("audio/mpeg");
      sourceBuffer.mode = "sequence";
      while (!terminal) {
        if (!owned()) { stop("owner_changed"); return; }
        const result = await iterator.next();
        if (!owned()) { if (!terminal) stop("owner_changed"); return; }
        if (result.done) {
          sourceDone = true;
          if (!first || !media || !sourceBuffer || sourceBuffer.updating || media.readyState !== "open") fail("protocol");
          media.endOfStream(); complete(); return;
        }
        const chunk = result.value;
        if (chunk.seq === nextSeq + 1 && chunk.part === 0 && nextPart > 0) { nextSeq++; nextPart = 0; clipBytes = 0; }
        if (chunk.seq !== nextSeq || chunk.part !== nextPart++ || nextSeq >= LIMITS.clipCount || nextPart > LIMITS.chunkCount
          || !chunk.bytes.length || chunk.bytes.length > LIMITS.chunkAudioBytes) fail("protocol");
        totalBytes += chunk.bytes.length; clipBytes += chunk.bytes.length;
        if (totalBytes > LIMITS.totalAudioBytes || clipBytes > LIMITS.clipAudioBytes) fail("invalid_audio");
        const buffer = sourceBuffer!;
        // Serial removal/append avoids updating races and bounds retained decoded history.
        if (audio.currentTime > 30 && buffer.buffered.length && buffer.buffered.start(0) < audio.currentTime - 15) {
          await waitEvent(buffer, "updateend", ["error", "abort"], () => buffer.remove(0, audio.currentTime - 15));
          if (!owned()) { if (!terminal) stop("owner_changed"); return; }
        }
        await waitEvent(buffer, "updateend", ["error", "abort"], () => buffer.appendBuffer(chunk.bytes.slice().buffer));
        if (!owned()) { if (!terminal) stop("owner_changed"); return; }
        if (!first) {
          first = true; observe({ stage: "first_audio_ready", cached: chunk.cached }); options.firstReady?.(chunk.cached);
          // Do not hold the producer on play(): some decoders require later parts.
          void admitPlayback().catch(error => failed(error instanceof ProgressiveSpeechFailure ? error : new ProgressiveSpeechFailure("playback")));
        }
      }
    } catch (error) { failed(error); }
  };
  const timer = setTimeout(() => failed(new ProgressiveSpeechFailure("timeout")), 600_000);
  const aborted = () => stop("stop");
  options.signal.addEventListener("abort", aborted, { once: true });
  void finished.then(() => { clearTimeout(timer); options.signal.removeEventListener("abort", aborted); });
  void pump();
  return { firstStarted, finished, cancel: stop };
}
